import { useTranslation } from 'react-i18next'

/**
 * Rotated rather than fixed, because the same two words after every answer is
 * what made the lesson feel like a form. Indexed by the parent so the line is
 * chosen once per answer and does not change under a re-render.
 */
const PRAISE_KEYS = [
  'pages.onboarding.v2.lesson.praise.nice',
  'pages.onboarding.v2.lesson.praise.correct',
  'pages.onboarding.v2.lesson.praise.greatJob',
  'pages.onboarding.v2.lesson.praise.amazing',
  'pages.onboarding.v2.lesson.praise.wellDone',
]

export function praiseKeyFor(answerNumber: number): string {
  return PRAISE_KEYS[answerNumber % PRAISE_KEYS.length]
}

/**
 * A tick or a cross, not the brand mark. Duolingo can put its mascot here
 * because the mascot has a face to pull; ours is the product logo, and a logo
 * next to the CONTINUE button reads as chrome rather than as a reaction to the
 * answer. The badge still carries the reaction through motion: a pop when
 * right, a head-shake when wrong.
 */
function ResultBadge({ correct }: { correct: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={
        correct
          ? 'animate-check-pop text-duo-green-text flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white'
          : 'animate-pair-shake text-duo-red-text flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white'
      }
    >
      <svg
        fill="none"
        height="24"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="3.5"
        viewBox="0 0 24 24"
        width="24"
      >
        {correct ? (
          <path d="M5 13l4.5 4.5L19 7" />
        ) : (
          <path d="M7 7l10 10M17 7L7 17" />
        )}
      </svg>
    </span>
  )
}

/**
 * The sheet that slides up over the footer once an answer is checked. Duolingo
 * tints the whole strip — green with a word of praise, red with the correct
 * solution — and recolours the CONTINUE button to match.
 */
export default function DuoFeedbackSheet({
  correct,
  onContinue,
  praiseKey,
  solution,
  streak,
}: {
  correct: boolean
  onContinue: () => void
  /** Which praise line to show; ignored when the answer was wrong. */
  praiseKey: string
  /** The expected answer, shown only when the learner got it wrong. */
  solution: string
  /** Consecutive correct answers, including this one. */
  streak: number
}) {
  const { t } = useTranslation()

  return (
    <div
      className={
        correct
          ? 'animate-fade-up bg-duo-green-soft -mx-4 px-4 pt-4 pb-6'
          : 'animate-fade-up bg-duo-red-soft -mx-4 px-4 pt-4 pb-6'
      }
      data-testid={
        correct ? 'lesson-feedback-correct' : 'lesson-feedback-wrong'
      }
    >
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <ResultBadge correct={correct} />

          {correct ? (
            <div>
              <p className="text-duo-green-text text-xl font-extrabold">
                {t(praiseKey)}
              </p>

              {/* Two is the first number that is actually a run. */}
              {streak >= 2 && (
                <p className="text-duo-green-text/80 text-sm font-bold">
                  {t('pages.onboarding.v2.lesson.combo', { count: streak })}
                </p>
              )}
            </div>
          ) : (
            <div>
              <p className="text-duo-red-text text-xl font-extrabold">
                {t('pages.onboarding.v2.lesson.wrong')}
              </p>

              <p className="text-duo-red-text mt-1 text-base font-bold">
                {solution}
              </p>
            </div>
          )}
        </div>

        <button
          className={correct ? 'duo-button-correct' : 'duo-button-wrong'}
          data-testid="button-lesson-continue"
          onClick={onContinue}
          type="button"
        >
          {t('shared.continue')}
        </button>
      </div>
    </div>
  )
}
